import {Component, OnDestroy, OnInit} from '@angular/core';
import {UsersService} from '@pt/api-client';
import {Subject} from 'rxjs';
import {FormControl, FormGroup} from '@angular/forms';
import {LoggedUser, PtUiAppRoutes} from '@pt/pt-models';
import {Store} from '@ngrx/store';
import {PtCoreActions, PtCoreSelectors, UrlUtils} from '@pt/pt-core';
import {PasswordParamsResponse} from '@primatoo-ms-organization';
import {takeUntil} from "rxjs/operators";
import {HttpErrorResponse} from "@angular/common/http";
import {ChangePasswordForLoginRequest} from "it/primatoo/openapi/api/org/schemas/user/ChangePasswordForLoginRequest";

@Component({
  selector: 'pt-change-password',
  template: `
      <div class="w-100 h-100 auth-layout">
          <div class="image-content">
              <pt-auth-img-area></pt-auth-img-area>
          </div>

          <div class="form-content">
              <ng-container sign>
                  <div class="change-password-container">
                      <div class="change-password-form">
                          <form [formGroup]="formGroup"
                                autocomplete="off"
                                (ngSubmit)="onSubmit()">
                              <h1>{{'AUTH.CHANGE_PASSWORD.TITLE' | translate}}</h1>
                              <p [innerHTML]="'AUTH.CHANGE_PASSWORD.DESCRIPTION' | translate"></p>
                              <ul [innerHTML]="'AUTH.CHANGE_PASSWORD.VALIDATORS' | translate"></ul>
                              <p [innerHTML]="'AUTH.CHANGE_PASSWORD.INFO' | translate"></p>

                              <pt-input-confirm-password
                                      formControlName="password"
                                      [controlName]="'password'"
                                      [passwordHelpParams]="passwordHelpParams"
                                      [passwordLabel]="'LABELS.PASSWORD' | translate"
                                      [confirmPasswordLabel]="'LABELS.CONFIRM_PASSWORD' | translate"
                              ></pt-input-confirm-password>

                              <ngb-alert *ngIf='errorMessage' [type]="'warning'" [dismissible]='false'
                                         [animation]='true'>
                                  {{ errorMessage | translate }}
                              </ngb-alert>

                              <div class="form-group row mt-4">
                                  <div class="col-12 text-end">
                                      <button type="button"
                                              (click)='cancel()'
                                              class="btn btn-outline-secondary btn-sm ms-2">
                                          {{'BUTTON.CANCEL' | translate}}
                                      </button>

                                      <button
                                              type="submit"
                                              class="btn btn-success btn-sm ms-2"
                                              [disabled]="formGroup.invalid || formGroup.pending">
                                          {{'BUTTON.SAVE' | translate}}
                                      </button>
                                  </div>
                              </div>
                          </form>
                      </div>
                  </div>
              </ng-container>
          </div>
      </div>
  `,
  styles: [`
    .change-password-container {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100vh;
      overflow-x: hidden;
      overflow-y: hidden;
      padding: 10px 30px;
      background-color: #324f86;
    }

    .change-password-container > * {
      width: 100%;
    }

    .change-password-form {
      padding: 40px;
      border-radius: 4px;
      background-color: var(--bs-light);
    }

    .auth-layout {
      display: grid;
      grid-template-columns: 1fr 650px;
      grid-template-rows: 1fr;
      gap: 0 0;
      grid-template-areas: "image-content form-content";
    }

    .form-content {
      grid-area: form-content;
      overflow: hidden;
    }

    .image-content {
      grid-area: image-content;
      overflow: hidden;
    }
  `]
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();

  loginResponse$ = this.store.select(PtCoreSelectors.loginResponse);

  formGroup: FormGroup;
  passwordHelpParams: PasswordParamsResponse;

  public errorMessage: string;

  private loginResponse: LoggedUser;

  get password() {
    return this.formGroup?.controls['password'];
  }

  constructor(public usersService: UsersService,
              private store: Store<any>) {
  }


  ngOnInit(): void {
    this.formGroup = new FormGroup({
      password: new FormControl(null)
    });

    this.loginResponse$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((response: LoggedUser) => {
      this.loginResponse = response;
    });

    setTimeout( () => {
      this.usersService.getPasswordHelpParams().subscribe((response) => {
        this.passwordHelpParams = response;
      });
    }, 1000)

  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  cancel() {
    this.store.dispatch(PtCoreActions.logoutAndClearData({
      route: [PtUiAppRoutes.LOGIN],
      domain: UrlUtils.getDomainIfExists()
    }));
  }

  onSubmit() {
    const request: ChangePasswordForLoginRequest = {
      newPassword: this.password.value
    };

    this.usersService.requiredChangePasswordRequest(request).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        if (this.loginResponse.twoFactorAuthEnabled && !this.loginResponse.hasOtpSecret) {
          this.store.dispatch(PtCoreActions.notificationTwoFactorAuthentication());
        }
        this.store.dispatch(PtCoreActions.navigateTo({route: [PtUiAppRoutes.LOADER]}));
      }, error: (e: HttpErrorResponse) => {
        if (e.error && e.error.reason) {
          this.errorMessage = e.error.reason;
        } else {
          this.errorMessage = 'MESSAGE.FORGOT_ERROR';
        }
      }
    });
  }

}
